import { supabase } from '../lib/supabase';

export interface PremiumOrder {
  id: string;
  user_id: string;
  package_type: 'badge' | 'featured' | 'editor_pick';
  duration_months: 1 | 3 | 6 | 12;
  price: number;
  status: 'pending' | 'completed' | 'cancelled';
  payment_method: string;
  payment_proof_url?: string;
  notes?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface CreateOrderData {
  package_type: 'badge' | 'featured' | 'editor_pick';
  duration_months: 1 | 3 | 6 | 12;
  price: number;
  notes?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
}

const packageNames: Record<string, string> = {
  badge: 'Rozet Ver',
  featured: 'Öne Çıkar',
  editor_pick: 'Editörün Seçimi',
};

const statusNames: Record<string, string> = {
  pending: 'Beklemede',
  completed: 'Onaylandı',
  cancelled: 'İptal Edildi',
};

export const ordersService = {
  async createOrder(
    data: CreateOrderData
  ): Promise<{ data: PremiumOrder | null; error: any }> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { data: null, error: { message: 'Giriş yapmalısınız' } };
    }

    const { data: order, error } = await supabase
      .from('premium_orders')
      .insert({
        user_id: user.id,
        package_type: data.package_type,
        duration_months: data.duration_months,
        price: data.price,
        payment_method: 'bank_transfer',
        notes: data.notes,
        contact_name: data.contact_name,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        status: 'pending',
      })
      .select()
      .single();

    if (order && !error) {
      // Admin'e yeni sipariş bildirimi gönder
      await ordersService.sendNewOrderNotification(order);
    }

    return { data: order, error };
  },

  async getUserOrders(): Promise<{ data: PremiumOrder[] | null; error: any }> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { data: null, error: { message: 'Giriş yapmalısınız' } };
    }

    const { data, error } = await supabase
      .from('premium_orders')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    return { data, error };
  },

  async getAllOrders(): Promise<{ data: any[] | null; error: any }> {
    const { data, error } = await supabase
      .from('premium_orders')
      .select(
        `
        *,
        profiles:user_id (
          username,
          email,
          avatar
        )
      `
      )
      .order('created_at', { ascending: false });

    return { data, error };
  },

  async getPendingOrderCount(): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('premium_orders')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) {
        console.error('Bekleyen sipariş sayısı alınamadı:', error);
        return 0;
      }

      return count ?? 0;
    } catch {
      return 0;
    }
  },

  async updateOrderStatus(
    orderId: string,
    status: 'completed' | 'cancelled',
    notes?: string
  ): Promise<{ data: PremiumOrder | null; error: any }> {
    const updateData: Record<string, any> = {
      status,
      updated_at: new Date().toISOString(),
    };

    if (status === 'completed') {
      updateData.completed_at = new Date().toISOString();
    }

    if (notes) {
      updateData.notes = notes;
    }

    const { data, error } = await supabase
      .from('premium_orders')
      .update(updateData)
      .eq('id', orderId)
      .select()
      .single();

    return { data, error };
  },

  // Yeni sipariş oluşturulduğunda admin'e bildirim gönder
  async sendNewOrderNotification(order: PremiumOrder): Promise<void> {
    const pkgName = packageNames[order.package_type] ?? order.package_type;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        console.warn('⚠️ Oturum bulunamadı, admin bildirimi gönderilemedi');
        return;
      }

      console.log('📤 Admin bildirimi gönderiliyor...', {
        orderId: order.id,
        email: order.contact_email,
        name: order.contact_name,
      });

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-order-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            orderId: order.id,
            userEmail: order.contact_email || 'Belirtilmemiş',
            userName: order.contact_name || 'Belirtilmemiş',
            packageName: pkgName,
            amount: order.price,
            isNewOrder: true,
            customerPhone: order.contact_phone,
          }),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ Admin bildirimi gönderilemedi:', result);
      } else {
        console.log('✅ Admin bildirimi başarıyla gönderildi:', result);
      }
    } catch (err) {
      console.error('❌ Admin e-posta bildirimi hatası:', err);
    }
  },

  // Sipariş durumu değiştiğinde kullanıcıya bildirim gönder
  async sendStatusNotification(
    order: PremiumOrder,
    newStatus: 'completed' | 'cancelled'
  ): Promise<void> {
    const email = order.contact_email;
    if (!email) {
      console.warn('Sipariş e-posta adresi bulunamadı, bildirim gönderilemedi');
      return;
    }

    const pkgName = packageNames[order.package_type] ?? order.package_type;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        console.warn('Oturum bulunamadı, e-posta gönderilemedi');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-order-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            orderId: order.id,
            userEmail: email,
            userName: order.contact_name || 'Değerli Müşterimiz',
            status: newStatus,
            packageName: pkgName,
            amount: order.price,
            isNewOrder: false,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('E-posta bildirimi gönderilemedi:', errorText);
      } else {
        const result = await response.json();
        console.log('E-posta bildirimi gönderildi:', result);
      }
    } catch (err) {
      console.error('E-posta bildirimi hatası:', err);
    }
  },

  async activatePremium(
    userId: string,
    durationMonths: number
  ): Promise<{ error: any }> {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('premium_until')
        .eq('id', userId)
        .maybeSingle();

      let premiumUntil = new Date();

      if (profile?.premium_until) {
        const existingDate = new Date(profile.premium_until);
        if (existingDate > new Date()) {
          premiumUntil = existingDate;
        }
      }

      premiumUntil.setMonth(premiumUntil.getMonth() + durationMonths);

      const { error } = await supabase
        .from('profiles')
        .update({
          is_premium: true,
          premium_until: premiumUntil.toISOString(),
        })
        .eq('id', userId);

      return { error };
    } catch (error) {
      return { error };
    }
  },

  async checkExpiredPremiums(): Promise<void> {
    try {
      const now = new Date().toISOString();

      await supabase
        .from('profiles')
        .update({ is_premium: false })
        .eq('is_premium', true)
        .lt('premium_until', now);
    } catch (error) {
      console.error('Premium kontrol hatası:', error);
    }
  },
};
